/**
 * FileMappingRow and FileMappingSection extracted from MappingStep.tsx.
 *
 * - FileMappingRow: a single CSV column → CRM property mapping row.
 * - FileMappingSection: collapsible (or static) card containing the full
 *   column-mapping table, default-stage picker, and stage-value mapping
 *   sub-view for one uploaded file.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle } from '@untitledui/icons';
import type { ChangeEvent, ReactNode } from 'react';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Key } from 'react-aria-components';

import { usePermissions } from '@/hooks/usePermissions';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { Skeleton } from '@/lib/base/skeleton';
import { Table, TableCard } from '@/lib/base/table';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { FieldTypeSelect } from '@/lib/crm/components/properties/FieldTypeSelect';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';
import type {
  ColumnMapping,
  CrmEntityAssociationMapping,
  NewPropertySuggestion,
  StageValueMapping as StageValueMappingType,
} from '@/lib/csv-import/api';
import { suggestStageValues } from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';
import { cx } from '@/utils/cx';

import type { ImportTypeOption } from '../../hooks/useImportTargetTypes';
import { useImportVocabulary } from '../../import-vocabulary';
import { SKIP_MARKER } from '../../useCsvImport';
import { getEntityTypeLabel } from '../../utils/entityTypeLabels';
import type { PropertyMappingOption } from '../PropertyMappingSelect';
import { PropertyMappingSelect } from '../PropertyMappingSelect';
import { ColumnLinkConfig } from './AssociationComponents';
import type { MoveTarget } from './ColumnActionsMenu';
import { ColumnActionsMenu } from './ColumnActionsMenu';
import { ColumnDragHandle } from './ColumnDragHandle';
import type {
  FileColumnInfo,
  PropertyTypeSchema,
  StageSuggestionState,
} from './mapping-constants';
import {
  COLUMN_TOOLTIPS,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  fetchPropertyTypes,
  getStageEntityTypeForSource,
  getStageLabelForSource,
  IMPORT_ENTITY_ID_COLUMN,
  isImportableProperty,
  LINK_SENTINEL,
  LOOKUP_FIELD_TYPES,
  SPLIT_MAPPING_OPTIONS,
  STAGE_SENTINEL,
} from './mapping-constants';
import { useStageValueSync } from './useStageValueSync';

// ============================================================================
// Record-matching select options (hoisted — stable across renders)
// ============================================================================

const MATCH_CREATE = 'create';
const MATCH_UPDATE = 'update';

const MATCH_OPTIONS = [
  {
    id: MATCH_CREATE,
    label: 'Create new',
    description: 'Always creates a new record',
  },
  {
    id: MATCH_UPDATE,
    label: 'Update on match',
    description:
      'Updates an existing record if one matches. Skipped if more than one record matches.',
  },
];

// ============================================================================
// FileMappingRow
// ============================================================================

export interface FileMappingRowProps {
  column: string;
  selectValue: string;
  isStageAlreadyMapped: boolean;
  hasStageSupport: boolean;
  stageLabel: string | undefined;
  allProperties: PropertyTypeSchema[];
  entityTypeName: string;
  onDropdownChange: (column: string, value: string) => void;
  onCreateProperty?: (csvColumn: string, label: string) => void;
  otherColumnsMappedToSameProperty: string[];
  currentProperty: PropertyTypeSchema | undefined;
  /** User-created new properties for this file — shown in dropdown. */
  userNewProperties?: NewPropertySuggestion[];
  /** Whether to render the field type column (true when any user new properties exist for this file). */
  showFieldTypeColumn: boolean;
  /** Called when the user changes a new property's field type. */
  onFieldTypeChange?: (propertyName: string, newFieldType: string) => void;
  /** True when the mapped property caused validation errors in the last analysis. */
  hasError?: boolean;
  /** Example error message from the last analysis for tooltip display. */
  errorMessage?: string;
  /** Called when the user clicks the options link on a new select property. */
  onConfigureSelectOptions?: (
    propertyName: string,
    entityTypeName: string
  ) => void;
  /** Org id, for the per-column link config's lookup-property fetch. */
  orgId: number;
  /** Schema-driven target entity types for the per-column "Link to" select. */
  targetTypeOptions: ImportTypeOption[];
  /** All association types for the org (from useAssociationMatchers). */
  associationTypes: AssociationType[];
  /** Kind-aware source/target matchers from useAssociationMatchers. */
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /** The link mapping for this column, when the column is in "Link to record" mode. */
  linkMapping?: CrmEntityAssociationMapping;
  /** Called with the updated link mapping for this column (config changes). */
  onLinkChange: (column: string, mapping: CrmEntityAssociationMapping) => void;
  /**
   * Draggable id (`${virtualFileId}::${column}`) for moving this column between
   * entity-type sections in a multi-type import. When set, a drag handle is
   * shown. Omitted for single-type files (no other section to move to).
   */
  dragId?: string;
  /** True when this row is currently being dragged. */
  isDragging?: boolean;
  /**
   * Optional actions element rendered as a trailing cell — used in multi-type
   * mapping to provide a per-row kebab menu (Remove / Move to {type}).
   */
  actions?: ReactNode;
  /** Whether this column is currently toggled on as a user match column. */
  isMatchColumn: boolean;
  /** Called when this column's record-matching selection changes. */
  onMatchColumnToggle: (csvColumn: string, enabled: boolean) => void;
  /** True when this column is used as a key in a cross-file association mapping. */
  isAssociationKey?: boolean;
}

export const FileMappingRow = memo(function FileMappingRow({
  column,
  selectValue,
  isStageAlreadyMapped,
  hasStageSupport,
  stageLabel,
  allProperties,
  entityTypeName,
  onDropdownChange,
  onCreateProperty,
  otherColumnsMappedToSameProperty,
  currentProperty,
  userNewProperties,
  showFieldTypeColumn,
  onFieldTypeChange,
  hasError,
  errorMessage,
  onConfigureSelectOptions,
  orgId,
  targetTypeOptions,
  associationTypes,
  matchesSource,
  matchesTarget,
  linkMapping,
  onLinkChange,
  dragId,
  isDragging,
  actions,
  isMatchColumn,
  onMatchColumnToggle,
  isAssociationKey,
}: FileMappingRowProps) {
  const isLinkMode = selectValue === LINK_SENTINEL;
  const isImportEntityIdMode = selectValue === IMPORT_ENTITY_ID_COLUMN;

  // Cell count for this row, which the expanded rows must span exactly. React
  // Aria's table is stricter than raw markup: a row whose cell count disagrees
  // with the header's throws rather than rendering a subtly shifted grid.
  const totalColumns = (showFieldTypeColumn ? 4 : 3) + (actions ? 1 : 0);

  const handleLinkConfigChange = useCallback(
    (mapping: CrmEntityAssociationMapping) => onLinkChange(column, mapping),
    [onLinkChange, column]
  );

  const handleLinkToRecord = useCallback(
    () => onDropdownChange(column, LINK_SENTINEL),
    [onDropdownChange, column]
  );
  const handleChange = useCallback(
    (value: string) => onDropdownChange(column, value),
    [onDropdownChange, column]
  );

  const handleNativeSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => handleChange(e.target.value),
    [handleChange]
  );

  const handleCreateProperty = useCallback(
    (label: string) => onCreateProperty?.(column, label),
    [onCreateProperty, column]
  );

  // Is the currently selected mapping a user-created new property?
  const activeUserNewProperty = useMemo(
    () => userNewProperties?.find(np => np.property_name === selectValue),
    [userNewProperties, selectValue]
  );

  // Is this column mapped to a property that will be CREATED on import (user- or
  // AI-suggested) rather than an existing one? True when the selected value is a
  // real property name that isn't a sentinel, a split mapping, or an existing
  // schema property. Drives the "New" badge so both kinds are obvious in the row.
  const isNewPropertyMapping = useMemo(() => {
    if (
      !selectValue ||
      selectValue === STAGE_SENTINEL ||
      selectValue === LINK_SENTINEL ||
      selectValue === SKIP_MARKER ||
      selectValue === IMPORT_ENTITY_ID_COLUMN ||
      selectValue.includes('+') // split mapping (maps to existing properties)
    ) {
      return false;
    }
    return !allProperties.some(p => p.property_type_name === selectValue);
  }, [selectValue, allProperties]);

  // Whether this column's record-matching option can be shown, and whether it's
  // forced on (unique properties always match) or left to the user's choice.
  const matchEligibility = useMemo<'forced' | 'eligible' | 'hidden'>(() => {
    if (!currentProperty) {
      return 'hidden';
    }
    if (currentProperty.is_unique_value) {
      return 'forced';
    }
    return LOOKUP_FIELD_TYPES.has(currentProperty.field_type_name)
      ? 'eligible'
      : 'hidden';
  }, [currentProperty]);
  const isMatchChecked = matchEligibility === 'forced' || isMatchColumn;

  const handleMatchChange = useCallback(
    (key: Key | null) =>
      onMatchColumnToggle(column, String(key) === MATCH_UPDATE),
    [onMatchColumnToggle, column]
  );

  // Self-heal a stale unique-match toggle. The toggle is only user-serializable
  // while the column maps to an eligible (non-unique, lookup-typed) property. If
  // a remap makes it ineligible — hidden (new/split/sentinel/unimportable type)
  // or forced (unique props are matched server-side, never sent) — drop it from
  // matchColumns so the serializer, review counts, and resume stay consistent.
  // The mapping table only renders once property types have loaded, so
  // matchEligibility is authoritative here and won't transiently clear a
  // resumed toggle while the schema is still in flight.
  useEffect(() => {
    if (isMatchColumn && matchEligibility !== 'eligible') {
      onMatchColumnToggle(column, false);
    }
  }, [isMatchColumn, matchEligibility, onMatchColumnToggle, column]);

  const handleFieldTypeChange = useCallback(
    (newFieldType: string) => {
      if (activeUserNewProperty) {
        onFieldTypeChange?.(activeUserNewProperty.property_name, newFieldType);
      }
    },
    [onFieldTypeChange, activeUserNewProperty]
  );

  // Is this column mapped to a new select property? Drives an options
  // affordance that is present for the whole life of the row. It must NOT be
  // conditioned on the options being empty: picking 'Dropdown' kicks off an
  // async suggest-options fetch, so the empty state lasts only until that
  // resolves (~200ms on a warm path). Gating the link (added in #11239) on it
  // made the only route to the options editor appear and then vanish under the
  // user, which read as "there is no way to configure options at all".
  const isNewSelectProperty = useMemo(
    () => activeUserNewProperty?.field_type_name === 'select',
    [activeUserNewProperty]
  );

  // Does this new select property still need options before it can import?
  // A select needs 2+ options to be usable, so this drives the warning
  // treatment on the affordance above — not whether it renders.
  const needsSelectOptions = useMemo(
    () =>
      Boolean(
        isNewSelectProperty &&
        (!activeUserNewProperty?.options ||
          activeUserNewProperty.options.length < 2)
      ),
    [isNewSelectProperty, activeUserNewProperty]
  );

  const handleConfigureOptions = useCallback(() => {
    if (activeUserNewProperty) {
      onConfigureSelectOptions?.(
        activeUserNewProperty.property_name,
        activeUserNewProperty.entity_type_name
      );
    }
  }, [onConfigureSelectOptions, activeUserNewProperty]);

  // Derive the field-type cell content: editable for new properties, disabled
  // for existing ones, dash for unmapped/skipped.
  const fieldTypeCell = useMemo(() => {
    if (activeUserNewProperty) {
      return {
        value: activeUserNewProperty.field_type_name,
        disabled: false,
        label: activeUserNewProperty.label,
      };
    }
    if (currentProperty) {
      return {
        value: currentProperty.field_type_name,
        disabled: true,
        label: currentProperty.label,
      };
    }
    return null;
  }, [activeUserNewProperty, currentProperty]);

  const splitOptions = useMemo(
    () => SPLIT_MAPPING_OPTIONS.filter(o => o.entityType === entityTypeName),
    [entityTypeName]
  );

  const options = useMemo<PropertyMappingOption[]>(() => {
    const result: PropertyMappingOption[] = [];

    // Stage option — only for supported entity types
    if (hasStageSupport && stageLabel) {
      result.push({
        id: STAGE_SENTINEL,
        label: stageLabel,
        group: 'stage',
        groupLabel: 'Stage',
        isDisabled: isStageAlreadyMapped,
        description: isStageAlreadyMapped
          ? 'Already mapped from another column'
          : undefined,
      });
    }

    // Split-mapping options (e.g. Full Name → First + Last)
    for (const opt of splitOptions) {
      result.push({
        id: opt.value,
        label: opt.label,
        group: 'combined',
        groupLabel: 'Combined',
      });
    }

    // CRM property options
    for (const prop of allProperties) {
      result.push({
        id: prop.property_type_name,
        label:
          prop.label +
          (prop.is_required ? ' *' : '') +
          (prop.is_multi ? ' (multiple values)' : ''),
        group: 'properties',
        groupLabel: 'Properties',
      });
    }

    // User-created new properties (proposed as part of the import)
    if (userNewProperties && userNewProperties.length > 0) {
      const existingIds = new Set(result.map(r => r.id));
      for (const np of userNewProperties) {
        if (!existingIds.has(np.property_name)) {
          result.push({
            id: np.property_name,
            label: `New · ${np.label}`,
            group: 'new_properties',
            groupLabel: 'New properties',
            description: np.column
              ? `Created from column "${np.column}"`
              : undefined,
          });
        }
      }
    }

    return result;
  }, [
    hasStageSupport,
    stageLabel,
    isStageAlreadyMapped,
    splitOptions,
    allProperties,
    userNewProperties,
  ]);

  return (
    <>
      {/* `h-auto` and the per-cell padding overrides keep the mapping grid at
          its existing density — the design-system defaults (h-14, px-5 py-3)
          are tuned for record tables, and applying them here would widen a grid
          that already needs a 480px minimum into horizontal scroll. cx is
          tailwind-merge, so these win over the component defaults. */}
      <Table.Row
        id={column}
        className={cx(
          'h-auto border-t border-border-tertiary',
          isDragging && 'bg-bg-secondary',
          hasError && !isDragging && 'bg-utility-error-50'
        )}
      >
        <Table.Cell className='px-0 py-2 text-sm text-fg-primary'>
          <span className='flex items-center gap-1.5'>
            {dragId && <ColumnDragHandle dragId={dragId} column={column} />}
            {hasError &&
              (errorMessage ? (
                <Tooltip title={errorMessage} placement='top'>
                  <TooltipTrigger aria-label={`Error: ${errorMessage}`}>
                    <AlertCircle
                      className='size-4 shrink-0 text-utility-error-500'
                      aria-hidden='true'
                    />
                  </TooltipTrigger>
                </Tooltip>
              ) : (
                <AlertCircle
                  className='size-4 shrink-0 text-utility-error-500'
                  aria-label='This mapping had errors'
                />
              ))}
            {column}
          </span>
        </Table.Cell>
        <Table.Cell className='px-0 py-2'>
          {/* Visually-hidden native select keeps property options in the
            accessibility tree so role="option" queries work in tests and
            AT without opening the custom popover. Only property options
            (not stage/split sentinels) are included to avoid duplicate
            text in the DOM. tabIndex={-1} removes it from the tab order. */}
          <select
            tabIndex={-1}
            className='sr-only'
            value={selectValue}
            onChange={handleNativeSelectChange}
            aria-label={`Map column ${column} to property`}
          >
            <option value=''>
              {actions ? 'Select property…' : 'Do not import'}
            </option>
            {allProperties.map(prop => (
              <option
                key={prop.property_type_name}
                value={prop.property_type_name}
              >
                {prop.label +
                  (prop.is_required ? ' *' : '') +
                  (prop.is_multi ? ' (multiple values)' : '')}
              </option>
            ))}
          </select>
          <div className='flex items-center gap-1.5'>
            {isImportEntityIdMode ? (
              <Tooltip
                title="Auto-detected. Rows are matched to existing records by this record ID; this mapping can't be changed."
                placement='top'
              >
                <TooltipTrigger>
                  <span className='text-sm text-fg-secondary'>
                    Match by Record ID
                  </span>
                </TooltipTrigger>
              </Tooltip>
            ) : (
              <PropertyMappingSelect
                value={selectValue}
                onChange={handleChange}
                options={options}
                placeholder={actions ? 'Select property' : 'Do not import'}
                showSkipOption={!actions}
                onCreateProperty={
                  onCreateProperty ? handleCreateProperty : undefined
                }
                newPropertyDefaultLabel={column}
                onLinkToRecord={handleLinkToRecord}
                ariaLabel={`Select property for column ${column}`}
              />
            )}
            {isNewPropertyMapping && (
              <Tooltip
                title='This property does not exist yet – it will be created when the import runs.'
                placement='top'
              >
                <TooltipTrigger aria-label='New property – will be created on import'>
                  <Badge type='pill-color' color='brand' size='sm'>
                    New
                  </Badge>
                </TooltipTrigger>
              </Tooltip>
            )}
            {isAssociationKey && (
              <Tooltip
                title='This column is used as a key to link records across files.'
                placement='top'
              >
                <TooltipTrigger aria-label='Cross-file association key'>
                  <Badge type='pill-color' color='gray' size='sm'>
                    Key
                  </Badge>
                </TooltipTrigger>
              </Tooltip>
            )}
            {otherColumnsMappedToSameProperty.length > 0 &&
              !currentProperty?.is_multi && (
                <Tooltip
                  title={`${otherColumnsMappedToSameProperty.join(', ')} also map${otherColumnsMappedToSameProperty.length === 1 ? 's' : ''} to this property and will be overwritten.`}
                  placement='top'
                >
                  <TooltipTrigger
                    aria-label={`Warning: ${otherColumnsMappedToSameProperty.join(', ')} also map${otherColumnsMappedToSameProperty.length === 1 ? 's' : ''} to this property and will be overwritten.`}
                  >
                    <AlertTriangle
                      className='size-4 shrink-0 text-fg-warning-primary'
                      aria-hidden='true'
                    />
                  </TooltipTrigger>
                </Tooltip>
              )}
          </div>
          {otherColumnsMappedToSameProperty.length > 0 &&
            currentProperty?.is_multi && (
              <p className='mt-1 text-xs text-fg-tertiary'>
                Also mapped from: {otherColumnsMappedToSameProperty.join(', ')}
              </p>
            )}
        </Table.Cell>
        {showFieldTypeColumn && (
          <Table.Cell className='px-2 py-2 align-middle'>
            {fieldTypeCell ? (
              <FieldTypeSelect
                value={fieldTypeCell.value}
                onChange={handleFieldTypeChange}
                disabled={fieldTypeCell.disabled}
                disabledReason={
                  fieldTypeCell.disabled
                    ? 'This is an existing property – its field type is set in the CRM schema.'
                    : undefined
                }
                aria-label={`Field type for ${fieldTypeCell.label}`}
              />
            ) : (
              <span className='text-sm text-fg-tertiary'>—</span>
            )}
          </Table.Cell>
        )}
        <Table.Cell className='px-2 py-2 align-middle'>
          {matchEligibility === 'hidden' ? (
            <span className='text-sm text-fg-quaternary'>Create new</span>
          ) : (
            <Select
              size='xs'
              selectedKey={isMatchChecked ? MATCH_UPDATE : MATCH_CREATE}
              onSelectionChange={handleMatchChange}
              isDisabled={matchEligibility === 'forced'}
              aria-label={`Record matching for column ${column}`}
              items={MATCH_OPTIONS}
              popoverPlacement='bottom end'
            >
              {item => <SelectItem {...item} />}
            </Select>
          )}
        </Table.Cell>
        {actions && (
          <Table.Cell className='w-8 py-2 pr-0 pl-1 align-middle'>
            {actions}
          </Table.Cell>
        )}
      </Table.Row>
      {isNewSelectProperty && (
        <Table.ExpandedRow
          id={`${column}-configure-options`}
          colSpan={totalColumns}
          className='bg-transparent'
          cellClassName='pb-2'
        >
          <Button
            type='button'
            color='link-color'
            size='sm'
            onClick={handleConfigureOptions}
            className={cx(
              '!h-auto !min-h-0 !p-0 !text-xs whitespace-nowrap',
              needsSelectOptions && '!text-[var(--color-text-warning-primary)]'
            )}
          >
            {needsSelectOptions ? 'Configure options →' : 'Edit options →'}
          </Button>
        </Table.ExpandedRow>
      )}
      {isLinkMode && linkMapping && (
        <Table.ExpandedRow
          id={`${column}-link-config`}
          colSpan={totalColumns}
          className='bg-transparent'
          cellClassName='pb-3'
        >
          <ColumnLinkConfig
            mapping={linkMapping}
            orgId={orgId}
            sourceEntityType={entityTypeName}
            targetTypeOptions={targetTypeOptions}
            associationTypes={associationTypes}
            matchesSource={matchesSource}
            matchesTarget={matchesTarget}
            onChange={handleLinkConfigChange}
          />
        </Table.ExpandedRow>
      )}
    </>
  );
});

// ============================================================================
// MappingTableSkeleton — shimmer placeholder matching the mapping table layout
// ============================================================================

/** Skeleton placeholder that mirrors the 4-column mapping table layout. */
function MappingTableSkeleton({ rows }: { rows: number }) {
  return (
    <div className='space-y-0'>
      {/* Header row */}
      <div className='flex gap-4 pb-2'>
        <Skeleton variant='text' width='28%' height={14} />
        <Skeleton variant='text' width='34%' height={14} />
        <Skeleton variant='text' width='18%' height={14} />
        <Skeleton variant='text' width='14%' height={14} />
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className='flex items-center gap-4 py-3'>
          <Skeleton variant='text' width='28%' height={20} />
          <Skeleton variant='rectangular' width='34%' height={36} />
          <Skeleton variant='text' width='18%' height={20} />
          <Skeleton variant='rectangular' width='14%' height={20} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// FileMappingSection
// ============================================================================

export interface FileMappingSectionProps {
  file: FileColumnInfo;
  orgId: number;
  mappings: ColumnMapping;
  handleMappingChange: (
    fileId: string | number,
    csvColumn: string,
    propertyName: string
  ) => void;
  isExpanded: boolean;
  onExpandedChange: (fileId: string | number, isOpen: boolean) => void;
  matchColumns: Record<string, string[]>;
  onMatchColumnToggle: (
    fileId: string | number,
    csvColumn: string,
    enabled: boolean
  ) => void;
  fileId: string | number;
  /**
   * Called when a user wants to add a custom property from the mapping
   * dropdown. Receives this section's `fileId` as `mappingKey` — the exact key
   * the section's rows read their column mappings from (the plain file id for a
   * standard file, the `${fileId}_virtual_${type}` editKey for a multi-type
   * section) — so the handler can write the new column mapping under the key
   * the UI and submit actually consume.
   */
  onCreateProperty?: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  /** User-created new properties for this file (shown in dropdown + field type column). */
  userNewProperties?: NewPropertySuggestion[];
  /** Called when a user-created new property's field type changes. */
  onUserNewPropertyFieldTypeChange?: (
    propertyName: string,
    newFieldType: string
  ) => void;
  /**
   * Validation errors from the most recent analysis, keyed by property name →
   * example error message. When present, rows mapping to these properties show
   * a warning indicator and a tooltip with the error.
   */
  errorProperties?: Record<string, string>;
  /** Called when the user clicks the options link on a new select property. */
  onConfigureSelectOptions?: (
    propertyName: string,
    entityTypeName: string
  ) => void;
  /** All CRM-entity link mappings (across files); filtered to this file internally. */
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  /** Called when the CRM-entity link mappings change. */
  onCrmEntityAssociationMappingsChange: (
    mappings: CrmEntityAssociationMapping[]
  ) => void;
  /** Schema-driven target entity types for the per-column "Link to" select. */
  targetTypeOptions: ImportTypeOption[];
  /** All association types for the org (from useAssociationMatchers). */
  associationTypes: AssociationType[];
  /** Kind-aware source/target matchers from useAssociationMatchers. */
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /**
   * Optional: when provided, "unskip" (restoring a skipped column) calls this
   * instead of clearing the mapping to ''. Multi-type type-sections use it to
   * drop the user override so the AI-suggested mapping becomes effective again.
   */
  onRestoreMapping?: (fileId: string | number, csvColumn: string) => void;
  /**
   * When set (multi-type entity-type sections), each row shows a drag handle so
   * the column can be moved to another entity type. The value is this section's
   * virtual file id, used to build per-column draggable ids.
   */
  dragSourceId?: string;
  /**
   * Optional content rendered at the top of the section body (above the
   * column-mapping table), inside the panel. Used to place the per-section
   * "Defaults for every imported record" panel (owner / visibility / default
   * stage) within each entity-type section.
   */
  sectionDefaults?: ReactNode;
  /**
   * Optional content rendered at the bottom of the section body (below the
   * column-mapping table), inside the panel. Used to place the per-section
   * "Link to the sections above" association controls within each entity-type
   * section, as a row under the property mappings.
   */
  sectionAssociations?: ReactNode;
  /**
   * Column name currently being dragged (from DndContext's activeColumn state).
   * When a row's column matches, it gets a subtle grey background.
   */
  draggedColumn?: string | null;
  /**
   * Entity types a column can be moved to via the per-row actions menu.
   * When provided (multi-type mode), each row renders a kebab menu with
   * "Remove" and "Move to {type}" actions. The list excludes the current
   * section's own type so columns can only move to other sections.
   */
  moveTargets?: MoveTarget[];
  /** Called when the user picks "Remove" from the row actions menu. */
  onRemoveColumn?: (column: string) => void;
  /** Called when the user picks "Move to {type}" from the row actions menu. */
  onMoveColumn?: (column: string, targetVirtualKey: string) => void;
  /** CSV columns used as cross-file association keys for this file. */
  associationKeyColumns?: ReadonlySet<string>;
  /**
   * The file's currently-selected default stage entity_id. Passed as a hint to
   * the stage-value-suggestion fetch so the backend scopes resolution to that
   * stage's pipeline. Re-fetches suggestions when it changes.
   */
  defaultStage?: string;
}

export function FileMappingSection({
  file,
  orgId,
  mappings,
  handleMappingChange,
  isExpanded,
  onExpandedChange,
  matchColumns,
  onMatchColumnToggle,
  fileId,
  onCreateProperty,
  userNewProperties,
  onUserNewPropertyFieldTypeChange,
  errorProperties,
  onConfigureSelectOptions,
  crmEntityAssociationMappings,
  onCrmEntityAssociationMappingsChange,
  targetTypeOptions,
  associationTypes,
  matchesSource,
  matchesTarget,
  onRestoreMapping,
  dragSourceId,
  sectionDefaults,
  sectionAssociations,
  draggedColumn,
  moveTargets,
  onRemoveColumn,
  onMoveColumn,
  associationKeyColumns,
  defaultStage,
}: FileMappingSectionProps) {
  const { isSchemaAdmin } = usePermissions();
  // A source-driven import has fields, not columns — see `import-vocabulary`.
  const vocabulary = useImportVocabulary();

  const onMappingChange = useCallback(
    (csvColumn: string, propertyName: string) =>
      handleMappingChange(fileId, csvColumn, propertyName),
    [fileId, handleMappingChange]
  );

  // Bind this section's fileId (the mapping key its rows read from — plain
  // file id or multi-type virtual editKey) so rows only supply (column, label).
  const onRowCreateProperty = useCallback(
    (csvColumn: string, label: string) =>
      onCreateProperty?.(fileId, csvColumn, label),
    [fileId, onCreateProperty]
  );

  // Fetch property types for this entity type
  const { data: propertyTypes = [], isLoading } = useQuery({
    queryKey: ['propertyTypes', orgId, file.entityTypeName],
    queryFn: () => fetchPropertyTypes(orgId, file.entityTypeName),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Stage suggestion state for this file
  const [stageSuggestionState, setStageSuggestionState] =
    useState<StageSuggestionState>({
      isLoading: false,
      error: null,
      suggestion: null,
    });

  // Filter to properties that can be mapped via CSV import: user-writable
  // properties ('all' write access) plus the sanctioned system timestamps
  // (ahoy_created_at, ahoy_last_activity_at — applied by the executor via
  // special mechanisms). Note member imports write as the user
  // (is_internal=False), so other 'internal' write-access properties (rollups
  // like potential-deal totals) are NOT importable — the backend analyzer
  // rejects them at review, so offering them here only manufactures errors.
  const allProperties = useMemo(
    () => propertyTypes.filter(isImportableProperty),
    [propertyTypes]
  );

  // Map of property names → error messages from the last analysis
  const errorPropertyMap = useMemo(
    () => errorProperties ?? {},
    [errorProperties]
  );

  // Map property_type_name → list of CSV columns currently mapped to it
  const propertyCounts = useMemo(() => {
    const counts: Record<string, string[]> = {};
    for (const [csvCol, propName] of Object.entries(mappings)) {
      if (!propName || propName === SKIP_MARKER) {
        continue;
      }
      (counts[propName] ??= []).push(csvCol);
    }
    return counts;
  }, [mappings]);

  // Derive human-readable label from entity_type_name (e.g., "ahoy_contact" -> "Contact")
  const entityTypeLabel = getEntityTypeLabel(file.entityTypeName);
  // Entity-type icon shown in the section header.
  const EntityIcon = getEntityTypeIconComponent(file.entityTypeName);

  // Determine if this file supports stage mapping
  const stageLabel = getStageLabelForSource(file.entityTypeName);
  const hasStageSupport = Boolean(stageLabel);
  // The stage entity type this file's stage column resolves against (e.g.
  // ahoy_deal -> ahoy_deal_pipeline_stage) — the `target_entity_type_name` a
  // stage-typed crm_entity_association_mappings entry for this file carries.
  const stageEntityTypeName = getStageEntityTypeForSource(file.entityTypeName);

  const fileIdStr = String(fileId);

  // The stage column mapping is a crm_entity_association_mappings entry whose
  // target_entity_type_name is this file's stage entity type — it carries
  // source_column + value_mappings (no target_property; see
  // CrmEntityAssociationMapping's doc comment). Stage is no longer a special
  // case: it's applied through the same generic association rail as owner/
  // company mappings (compare to linkMappingByColumn below).
  const stageAssociationMapping = useMemo(
    () =>
      stageEntityTypeName
        ? crmEntityAssociationMappings.find(
            m =>
              String(m.source_file_id) === fileIdStr &&
              m.target_entity_type_name === stageEntityTypeName
          )
        : undefined,
    [crmEntityAssociationMappings, fileIdStr, stageEntityTypeName]
  );
  const stageMappedColumn = stageAssociationMapping?.source_column ?? '';
  const stageValueMappings =
    stageAssociationMapping?.value_mappings ?? EMPTY_OBJECT;

  // Derive ambiguous stage values from the suggestion — stage names that matched
  // more than one pipeline and were left unmapped by the backend.
  const ambiguousStages = useMemo(
    () => stageSuggestionState.suggestion?.ambiguous_stages ?? [],
    [stageSuggestionState.suggestion]
  );

  const ambiguousStagesDescription = useMemo(() => {
    if (ambiguousStages.length === 0) {
      return '';
    }
    const MAX = 5;
    const shown = ambiguousStages.slice(0, MAX);
    const remaining = ambiguousStages.length - shown.length;
    const valueList =
      shown.join(', ') + (remaining > 0 ? `, +${remaining} more` : '');
    return `${valueList}. Set a default stage for this file to choose the pipeline, or they'll be left for you to assign.`;
  }, [ambiguousStages]);

  // Find if ANY other column for this file is already mapped to stage
  // (used to disable the stage option when another column has claimed it)
  const stageAlreadyMappedToOtherColumn = useCallback(
    (csvColumn: string): boolean => {
      return Boolean(stageMappedColumn && stageMappedColumn !== csvColumn);
    },
    [stageMappedColumn]
  );

  // Replace (or clear) this file's stage-typed crm_entity_association_mappings
  // entry — the single write path for the stage column/value_mappings. Mirrors
  // handleLinkChange/handleRemoveLink below for regular association mappings.
  const replaceStageAssociationMapping = useCallback(
    (entry: CrmEntityAssociationMapping | null) => {
      const next = crmEntityAssociationMappings.filter(
        m =>
          !(
            String(m.source_file_id) === fileIdStr &&
            m.target_entity_type_name === stageEntityTypeName
          )
      );
      if (entry) {
        next.push(entry);
      }
      onCrmEntityAssociationMappingsChange(next);
    },
    [
      crmEntityAssociationMappings,
      fileIdStr,
      stageEntityTypeName,
      onCrmEntityAssociationMappingsChange,
    ]
  );

  const fetchStageSuggestions = useCallback(
    async (columnName: string) => {
      // Store original state before applying suggestions
      const originalValueMappings =
        stageAssociationMapping?.value_mappings ?? {};
      const originalColumn = stageAssociationMapping?.source_column;

      setStageSuggestionState({
        isLoading: true,
        error: null,
        suggestion: null,
      });
      try {
        const suggestion = await suggestStageValues(
          orgId,
          fileId,
          columnName,
          file.entityTypeName,
          defaultStage
        );
        setStageSuggestionState({ isLoading: false, error: null, suggestion });

        // Commit non-null AI suggestions into value_mappings so they're
        // included in the payload even if the user doesn't touch the dropdowns.
        // Without this, suggestions are only used for visual display and the
        // backend receives empty value_mappings, causing all rows to get the
        // default stage.
        if (suggestion?.suggested_mappings && stageEntityTypeName) {
          const autoMappings: Record<string, string> = {};
          for (const [csvValue, stageId] of Object.entries(
            suggestion.suggested_mappings
          )) {
            if (stageId) {
              autoMappings[csvValue] = stageId;
            }
          }
          if (Object.keys(autoMappings).length > 0) {
            replaceStageAssociationMapping({
              source_file_id: fileId,
              source_column: columnName,
              target_entity_type_name: stageEntityTypeName,
              target_property: '',
              value_mappings: {
                ...autoMappings,
                // Preserve any existing user overrides
                ...originalValueMappings,
              },
            });
          }
        }
      } catch (err) {
        logger.error(
          'Failed to load stage suggestions',
          err instanceof Error ? err : new Error(String(err))
        );
        setStageSuggestionState(prev => ({
          isLoading: false,
          error: "We couldn't load the stage suggestions. Try again.",
          suggestion: prev.suggestion, // Keep any previous suggestion for fallback
        }));

        // Rollback to original mappings on error (preserve original column, not the
        // potentially empty columnName that triggered this call). Skip when
        // neither is set — e.g. the auto-fetch-on-expand probe (empty
        // columnName, no prior stage entry) — so a failed probe doesn't create
        // a degenerate entry with an empty source_column.
        if (stageEntityTypeName && (originalColumn || columnName)) {
          replaceStageAssociationMapping({
            source_file_id: fileId,
            source_column: originalColumn || columnName,
            target_entity_type_name: stageEntityTypeName,
            target_property: '',
            value_mappings: originalValueMappings,
          });
        }
      }
    },
    [
      orgId,
      fileId,
      file.entityTypeName,
      defaultStage,
      stageEntityTypeName,
      stageAssociationMapping,
      replaceStageAssociationMapping,
    ]
  );

  // Fetch available stages when section expands for default stage selection
  // (even when no stage column is mapped)
  useEffect(() => {
    if (
      isExpanded &&
      hasStageSupport &&
      !stageSuggestionState.suggestion &&
      !stageSuggestionState.isLoading &&
      !stageSuggestionState.error
    ) {
      // Fetch stages with empty column - API returns available_stages without needing column values
      fetchStageSuggestions('');
    }
  }, [
    isExpanded,
    hasStageSupport,
    stageSuggestionState.suggestion,
    stageSuggestionState.isLoading,
    stageSuggestionState.error,
    fetchStageSuggestions,
  ]);

  // Re-fetch stage suggestions when the file's default stage changes, so the
  // backend re-scopes resolution to the new stage's pipeline. Guarded so this
  // only reacts to an actual CHANGE (never the initial mount value) and only
  // when a stage column is mapped with an existing suggestion to invalidate —
  // this is a response to the user picking a different default stage, not a
  // self-triggering effect (clearing `suggestion` doesn't feed back into
  // `defaultStage`), so it can't loop.
  const previousDefaultStageRef = useRef(defaultStage);
  useEffect(() => {
    const previousDefaultStage = previousDefaultStageRef.current;
    previousDefaultStageRef.current = defaultStage;
    if (
      previousDefaultStage !== defaultStage &&
      stageMappedColumn &&
      stageSuggestionState.suggestion !== null
    ) {
      // Fetch with the actual column name so the backend re-resolves
      // value_mappings against the new pipeline. fetchStageSuggestions
      // sets isLoading: true internally and has defaultStage in its closure.
      void fetchStageSuggestions(stageMappedColumn);
    }
  }, [
    defaultStage,
    stageMappedColumn,
    stageSuggestionState.suggestion,
    fetchStageSuggestions,
  ]);

  // Link ("Link to record") mappings for this file, keyed by source column.
  // Excludes this file's stage-typed entry — that one is tracked separately via
  // stageAssociationMapping/stageMappedColumn above, and getSelectValue checks
  // this map before the stage column, so a stage entry leaking in here would
  // render as "Link to record" instead of "Stage".
  const linkMappingByColumn = useMemo(() => {
    const map: Record<string, CrmEntityAssociationMapping> = {};
    for (const m of crmEntityAssociationMappings) {
      if (
        String(m.source_file_id) === String(fileId) &&
        m.target_entity_type_name !== stageEntityTypeName
      ) {
        map[m.source_column] = m;
      }
    }
    return map;
  }, [crmEntityAssociationMappings, fileId, stageEntityTypeName]);

  // Replace (or insert) the link mapping for a column.
  const handleLinkChange = useCallback(
    (csvColumn: string, mapping: CrmEntityAssociationMapping) => {
      const next = crmEntityAssociationMappings.filter(
        m =>
          !(
            String(m.source_file_id) === String(fileId) &&
            m.source_column === csvColumn
          )
      );
      next.push(mapping);
      onCrmEntityAssociationMappingsChange(next);
    },
    [crmEntityAssociationMappings, fileId, onCrmEntityAssociationMappingsChange]
  );

  // Remove the link mapping for a column (when leaving link mode).
  const handleRemoveLink = useCallback(
    (csvColumn: string) => {
      onCrmEntityAssociationMappingsChange(
        crmEntityAssociationMappings.filter(
          m =>
            !(
              String(m.source_file_id) === String(fileId) &&
              m.source_column === csvColumn
            )
        )
      );
    },
    [crmEntityAssociationMappings, fileId, onCrmEntityAssociationMappingsChange]
  );

  const handleDropdownChange = useCallback(
    (csvColumn: string, selectedValue: string) => {
      const wasStageColumn = stageMappedColumn === csvColumn;

      if (selectedValue === STAGE_SENTINEL) {
        // Map this column to stage: a single filter+push replaces (a) any prior
        // stage entry for this file (reassigning which column is the stage
        // column) and (b) any prior non-stage entry for this exact column (e.g.
        // it was in Link mode) — combined into one update, since a second
        // onCrmEntityAssociationMappingsChange call in this same invocation
        // would overwrite this one with a stale snapshot.
        if (stageEntityTypeName) {
          const preservedValueMappings =
            stageAssociationMapping?.value_mappings ?? {};
          const next = crmEntityAssociationMappings.filter(
            m =>
              !(
                String(m.source_file_id) === fileIdStr &&
                m.target_entity_type_name === stageEntityTypeName
              ) &&
              !(
                String(m.source_file_id) === String(fileId) &&
                m.source_column === csvColumn
              )
          );
          next.push({
            source_file_id: fileId,
            source_column: csvColumn,
            target_entity_type_name: stageEntityTypeName,
            target_property: '',
            value_mappings: preservedValueMappings,
          });
          onCrmEntityAssociationMappingsChange(next);
        }

        // Remove this column from column mappings if it was previously a property mapping
        onMappingChange(csvColumn, '');

        // Trigger stage value suggestions
        fetchStageSuggestions(csvColumn);
        return;
      }

      if (selectedValue === LINK_SENTINEL) {
        // Enter link mode: clear the column's property mapping and seed an empty
        // link mapping to be configured in the disclosure below. If this column
        // was the stage column, drop its stage entry in the SAME update — see
        // the STAGE_SENTINEL branch above for why these can't be two calls.
        onMappingChange(csvColumn, '');
        if (!linkMappingByColumn[csvColumn]) {
          const base = wasStageColumn
            ? crmEntityAssociationMappings.filter(
                m =>
                  !(
                    String(m.source_file_id) === fileIdStr &&
                    m.target_entity_type_name === stageEntityTypeName
                  )
              )
            : crmEntityAssociationMappings;
          onCrmEntityAssociationMappingsChange([
            ...base,
            {
              source_file_id: fileId,
              source_column: csvColumn,
              target_entity_type_name: '',
              target_property: '',
            },
          ]);
        } else if (wasStageColumn) {
          replaceStageAssociationMapping(null);
        }
        return;
      }

      // Leaving the stage column for a plain property/skip/restore selection.
      if (wasStageColumn) {
        replaceStageAssociationMapping(null);
      }

      // Any other selection — leave link mode if it was active.
      if (linkMappingByColumn[csvColumn]) {
        handleRemoveLink(csvColumn);
      }
      if (selectedValue === '' && onRestoreMapping) {
        onRestoreMapping(fileId, csvColumn);
      } else {
        onMappingChange(csvColumn, selectedValue);
      }
    },
    [
      stageEntityTypeName,
      stageMappedColumn,
      stageAssociationMapping,
      replaceStageAssociationMapping,
      crmEntityAssociationMappings,
      fileId,
      fileIdStr,
      onCrmEntityAssociationMappingsChange,
      onMappingChange,
      fetchStageSuggestions,
      linkMappingByColumn,
      handleRemoveLink,
      onRestoreMapping,
    ]
  );

  // Get the select value for a given csv column
  const getSelectValue = useCallback(
    (csvColumn: string): string => {
      if (linkMappingByColumn[csvColumn]) {
        return LINK_SENTINEL;
      }
      if (stageMappedColumn === csvColumn) {
        return STAGE_SENTINEL;
      }
      const v = mappings[csvColumn];
      return v === SKIP_MARKER ? '' : (v ?? '');
    },
    [linkMappingByColumn, stageMappedColumn, mappings]
  );

  const handleAccordionExpandedChange = useCallback(
    (isOpen: boolean) => onExpandedChange(fileId, isOpen),
    [fileId, onExpandedChange]
  );

  const handleValueMappingsChange = useCallback(
    (newValueMappings: StageValueMappingType) => {
      if (!stageEntityTypeName) {
        return;
      }
      replaceStageAssociationMapping({
        source_file_id: fileId,
        source_column: stageMappedColumn,
        target_entity_type_name: stageEntityTypeName,
        target_property: '',
        value_mappings: newValueMappings,
      });
    },
    [
      stageEntityTypeName,
      fileId,
      stageMappedColumn,
      replaceStageAssociationMapping,
    ]
  );

  const handleFetchSuggestions = useCallback(
    () => fetchStageSuggestions(stageMappedColumn),
    [fetchStageSuggestions, stageMappedColumn]
  );

  // Sort columns: mapped columns first (preserving original order within each
  // group), unmapped/skipped columns last. This keeps the user's attention on
  // the actionable mappings and tucks "Do not import" columns out of the way.
  // The trailing per-row actions menu, or null when this section has no row
  // actions. Both the header cell and the row cells derive from this ONE value,
  // because they must agree exactly: the old markup keyed the header off
  // `moveTargets` alone while the rows also required both handlers, so passing
  // `moveTargets` without them produced a header column no row ever filled. Raw
  // <table> absorbed that silently; React Aria's table does not.
  const renderRowActions = useMemo(() => {
    if (!moveTargets || !onRemoveColumn || !onMoveColumn) {
      return null;
    }
    return (column: string) => (
      <ColumnActionsMenu
        column={column}
        moveTargets={moveTargets}
        onRemove={onRemoveColumn}
        onMoveTo={onMoveColumn}
      />
    );
  }, [moveTargets, onRemoveColumn, onMoveColumn]);
  const showActionsColumn = renderRowActions !== null;

  const sortedColumns = useMemo(() => {
    const mapped: string[] = [];
    const unmapped: string[] = [];
    for (const column of file.columns) {
      const sv = getSelectValue(column);
      if (sv === '') {
        unmapped.push(column);
      } else {
        mapped.push(column);
      }
    }
    return [...mapped, ...unmapped];
  }, [file.columns, getSelectValue]);

  // Pre-compute per-column "other columns mapped to same property" so the
  // render loop doesn't create new array references per iteration.
  const otherColumnsByColumn = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const column of file.columns) {
      const sv = getSelectValue(column);
      const all = propertyCounts[sv] ?? EMPTY_ARRAY;
      result[column] =
        all.length > 1 ? all.filter(c => c !== column) : EMPTY_ARRAY;
    }
    return result;
  }, [file.columns, getSelectValue, propertyCounts]);

  // Pre-compute property lookup by name to avoid O(n) .find() per column in render.
  // Uses the UNFILTERED propertyTypes so that field type labels display for all
  // mapped properties — including internal ones (ahoy_email, ahoy_name, etc.)
  // that are excluded from the dropdown options by the write-access filter.
  const propertyByName = useMemo(() => {
    const map = new Map<string, PropertyTypeSchema>();
    for (const p of propertyTypes) {
      map.set(p.property_type_name, p);
    }
    return map;
  }, [propertyTypes]);

  // Columns toggled on as unique-match columns for this section.
  const matchColumnsForFile = useMemo(
    () => new Set(matchColumns[String(fileId)] ?? EMPTY_ARRAY),
    [matchColumns, fileId]
  );

  const handleMatchToggle = useCallback(
    (csvColumn: string, enabled: boolean) =>
      onMatchColumnToggle(fileId, csvColumn, enabled),
    [onMatchColumnToggle, fileId]
  );

  // The stage value-mapping block, rendered either inline under the stage
  // column's row (the common case) or, as a fallback below the table, when that
  // column is no longer a row in this section — e.g. it was dragged to another
  // type, or (on resume) the stage column was never a property-mapping row. Both
  // call sites reuse this single element; only one branch is ever truthy, so the
  // same descriptor is never mounted in two places at once.
  // No UI: the per-value override list was removed. AI suggestions are still
  // auto-committed into value_mappings above, and anything unmatched takes the
  // fallback from the Fallback Values panel. This hook carries the two effects
  // that list used to own — see useStageValueSync for why both are load-bearing.
  useStageValueSync({
    orgId,
    entityTypeName: file.entityTypeName,
    hasStageSupport,
    column: hasStageSupport ? stageMappedColumn : '',
    valueMappings: stageValueMappings,
    onValueMappingsChange: handleValueMappingsChange,
    onFetchSuggestions: handleFetchSuggestions,
    isLoading: stageSuggestionState.isLoading,
    error: stageSuggestionState.error,
    hasFetchedSuggestions: stageSuggestionState.suggestion !== null,
  });

  const bodyContent = isLoading ? (
    <div aria-busy='true'>
      <MappingTableSkeleton rows={Math.min(file.columns.length, 8)} />
    </div>
  ) : (
    <>
      {/* The deleted stage-value list used to own this message. Without it a
          failed suggest-stage-values call is invisible, and because suggestions
          are what populate value_mappings, every row would silently take the
          fallback stage with nothing on screen to explain why. */}
      {stageSuggestionState.error && (
        <div className='mb-3'>
          <AlertFloating
            title="We couldn't read the stage values"
            description={`Every row will use the fallback stage for ${vocabulary.columnHeading.toLowerCase()} "${stageMappedColumn}". Re-pick the column to try again.`}
            color='warning'
          />
        </div>
      )}
      {ambiguousStages.length > 0 && stageMappedColumn && !defaultStage && (
        <div className='mb-3' role='status'>
          <AlertFloating
            title='These values match stages in more than one pipeline'
            description={ambiguousStagesDescription}
            color='warning'
          />
        </div>
      )}
      {/* TableCard.ScrollArea replaces the local ScrollableTableWrapper here: it
          is the same gradient-edged horizontal scroller, and it also publishes
          the --table-scroll-area-width variable that Table.ExpandedRow needs to
          stay pinned when a wide grid is scrolled sideways. */}
      <TableCard.ScrollArea>
        <Table
          size='sm'
          aria-label={`Column mapping for ${file.filename}`}
          className='min-w-[480px]'
        >
          <Table.Header>
            <Table.Row>
              {/* Table.Head carries label + tooltip natively, which is exactly
                  what the local ColumnHeader was hand-rolling. */}
              <Table.Head
                id='csv-column'
                isRowHeader
                className='px-0 pb-2'
                label={vocabulary.columnHeading}
                tooltip={vocabulary.columnTooltip}
              />
              <Table.Head
                id='map-to-property'
                className='px-0 pb-2'
                label='Map to property'
                tooltip={
                  isSchemaAdmin
                    ? vocabulary.mapToPropertyTooltipAdmin
                    : vocabulary.mapToPropertyTooltipNonAdmin
                }
              />
              <Table.Head
                id='field-type'
                className='px-2 pb-2'
                label='Field type'
                tooltip={COLUMN_TOOLTIPS.fieldType}
              />
              <Table.Head
                id='record-matching'
                className='px-2 pb-2 whitespace-nowrap'
                label='Record matching'
                tooltip={vocabulary.recordMatchingTooltip}
              />
              {showActionsColumn && (
                <Table.Head id='row-actions' className='w-8 px-0 pb-2' />
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedColumns.map(column => {
              const sv = getSelectValue(column);
              return (
                <Fragment key={column}>
                  <FileMappingRow
                    column={column}
                    selectValue={sv}
                    isStageAlreadyMapped={stageAlreadyMappedToOtherColumn(
                      column
                    )}
                    hasStageSupport={hasStageSupport}
                    stageLabel={stageLabel}
                    allProperties={allProperties}
                    entityTypeName={file.entityTypeName}
                    onDropdownChange={handleDropdownChange}
                    onCreateProperty={
                      onCreateProperty ? onRowCreateProperty : undefined
                    }
                    otherColumnsMappedToSameProperty={
                      otherColumnsByColumn[column] ?? EMPTY_ARRAY
                    }
                    currentProperty={propertyByName.get(sv)}
                    userNewProperties={userNewProperties}
                    showFieldTypeColumn
                    onFieldTypeChange={onUserNewPropertyFieldTypeChange}
                    hasError={sv !== '' && sv in errorPropertyMap}
                    errorMessage={
                      sv !== '' && sv in errorPropertyMap
                        ? errorPropertyMap[sv]
                        : undefined
                    }
                    onConfigureSelectOptions={onConfigureSelectOptions}
                    orgId={orgId}
                    targetTypeOptions={targetTypeOptions}
                    associationTypes={associationTypes}
                    matchesSource={matchesSource}
                    matchesTarget={matchesTarget}
                    linkMapping={linkMappingByColumn[column]}
                    onLinkChange={handleLinkChange}
                    dragId={
                      dragSourceId ? `${dragSourceId}::${column}` : undefined
                    }
                    isDragging={draggedColumn === column}
                    actions={renderRowActions?.(column)}
                    isMatchColumn={matchColumnsForFile.has(column)}
                    onMatchColumnToggle={handleMatchToggle}
                    isAssociationKey={associationKeyColumns?.has(column)}
                  />
                </Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </TableCard.ScrollArea>

      {sectionAssociations != null && (
        <div className='mt-4'>{sectionAssociations}</div>
      )}

      {sectionDefaults != null && <div className='mt-4'>{sectionDefaults}</div>}
    </>
  );

  return (
    <AccordionItem
      isExpanded={isExpanded}
      onExpandedChange={handleAccordionExpandedChange}
      aria-label={`Map columns for ${file.filename} (${entityTypeLabel})`}
      className='border border-border-secondary rounded-lg bg-bg-primary'
    >
      <AccordionTrigger className='w-full px-4 py-3 flex items-center justify-between hover:bg-bg-secondary text-left'>
        <div className='flex items-center gap-3'>
          <AccordionChevron
            variant='swap'
            className='w-5 h-5 text-fg-quaternary'
          />
          <EntityIcon className='size-4 shrink-0 text-fg-quaternary' />
          <div className='text-left'>
            <span className='font-medium text-fg-primary'>
              {entityTypeLabel}
            </span>
            <span className='ml-2 text-sm text-fg-tertiary'>
              {file.filename}
            </span>
          </div>
        </div>
        <span className='text-sm text-fg-quaternary'>
          {file.columns.length} columns
        </span>
      </AccordionTrigger>

      <AccordionPanel className='border-t border-border-secondary px-4 py-4'>
        {bodyContent}
      </AccordionPanel>
    </AccordionItem>
  );
}
